import { useMemo, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import styled from "styled-components";
import { z } from "zod";
import { Button } from "../../atoms/Button";
import { Input } from "../../atoms/Input";
import FormTemplate from "../../templates/FormTemplate";
import { useButtonState } from "../../../hooks/useButtonState";

type Props = {
  handleSubmit: (password: string, onError: (message: string) => void) => void;
};

export const ResetPasswordForm: React.FC<Props> = ({ handleSubmit }) => {
  const [hasPasswordError, setHasPasswordError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  // Use button state hook to prevent double clicks
  const { isSubmitting, executeWithLoading } = useButtonState();

  const schema = useMemo(() => {
    return z
      .object({
        password: z.string().min(1),
        passwordConfirmation: z.string().min(1),
      })
      .refine((data) => data.password === data.passwordConfirmation);
  }, []);

  const [methods, setMethods] = useState<UseFormReturn<
    FieldValues,
    any,
    undefined
  > | null>(null);

  return (
    <FormTemplate
      schema={schema}
      setMethods={setMethods}
      isSubmitting={isSubmitting}
      defaultValues={{ password: "", passwordConfirmation: "" }}
      onSubmit={({ password }) => {
        // Prevent submission if already submitting
        if (isSubmitting) {
          return;
        }

        // Use executeWithLoading to prevent double submissions
        executeWithLoading(async () => {
          // Wrap handleSubmit in a Promise to work with executeWithLoading
          return new Promise<void>((resolve, reject) => {
            handleSubmit(password, (message) => {
              setHasPasswordError(true);
              setErrorMessage(message);
              reject(new Error(message));
            });

            // For successful submissions, resolve the promise
            setTimeout(resolve, 100);
          });
        }).catch((error) => {
          // Error is already handled by setting error state above
          console.log('ResetPassword form submission error handled:', error.message);
        });
      }}
    >
      <StyledTextContainer>
        Enter a new password below to change your password.
      </StyledTextContainer>

      <StyledInputContainer>
        <Input
          label="New Password"
          name="password"
          type="password"
          autoFocus
          required
          hasError={hasPasswordError}
          errorMessage={errorMessage}
        />
        <Input
          label="Confirm New Password"
          name="passwordConfirmation"
          type="password"
          required
          hasError={hasPasswordError}
        />
      </StyledInputContainer>

      <StyledFooter>
        <Button variant="filled" text="Save" type="submit" fullWidth isSubmitting={isSubmitting} />
      </StyledFooter>
    </FormTemplate>
  );
};

const StyledTextContainer = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  margin-bottom: 24px;
  color: #697077;
`;

const StyledInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StyledFooter = styled.div`
  margin-top: 32px;
`;
